<template>
  <header class="fixed top-0 w-full z-40 glass-nav border-b border-[#1c4848]/5 transition-all">
    <div class="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
      <div class="flex items-center gap-3 shrink-0">
        <div
          class="w-10 h-10 rounded-xl bg-[#1c4848] flex items-center justify-center shadow-lg overflow-hidden p-1"
        >
          <img :src="logoYellowUrl" alt="Deenly" class="w-full h-full object-contain" />
        </div>
        <span
          class="text-xl font-serif text-[#1c4848] font-semibold tracking-tight hidden sm:block"
        >
          {{ t('home.header.brand') }}
        </span>
      </div>

      <div
        v-if="prayers.length"
        class="hidden lg:flex items-center gap-6 bg-white/50 px-6 py-2 rounded-2xl border border-[#1c4848]/5 shadow-sm"
      >
        <template v-for="(p, idx) in prayers" :key="p.key">
          <div class="flex flex-col items-center" :class="{ relative: p.key === nextKey }">
            <span
              v-if="p.key === nextKey"
              class="absolute -top-1 -right-2 w-1.5 h-1.5 rounded-full bg-[#ecd79f] animate-pulse"
            ></span>
            <span class="text-xs text-[#1c4848]/40 font-semibold uppercase tracking-widest">
              {{ prayerLabel(p.key) }}
            </span>
            <span class="text-sm font-semibold text-[#1c4848]">{{ p.time }}</span>
          </div>
          <div v-if="idx !== prayers.length - 1" class="w-px h-6 bg-[#1c4848]/10"></div>
        </template>
      </div>

      <div class="flex items-center gap-2 sm:gap-3 shrink-0">
        <LanguageMenu
          button-class="h-10 px-3 rounded-2xl text-xs font-bold uppercase tracking-widest text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5 transition-colors"
          @select="setLocale"
        />
        <router-link
          to="/akademija"
          class="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold bg-white/60 border border-[#1c4848]/10 text-[#1c4848] shadow-sm hover:bg-white hover:shadow-md transition-all"
        >
          <iconify-icon icon="solar:square-academic-cap-linear" class="text-lg" aria-hidden="true"></iconify-icon>
          {{ t('study.common.academy') }}
        </router-link>
        <router-link
          :to="quranPath()"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold bg-deen-ink text-white shadow-sm hover:bg-deen-ink/90 transition-all"
        >
          <iconify-icon icon="solar:book-2-linear" class="text-lg" aria-hidden="true"></iconify-icon>
          {{ t('quran.nav.quran') }}
        </router-link>
        <button
          type="button"
          class="hidden md:inline-flex items-center gap-2 bg-white/60 px-4 py-2 rounded-2xl border border-[#1c4848]/10 shadow-sm text-sm font-semibold text-[#1c4848] hover:bg-white hover:shadow-md transition-all"
          @click="showCityModal = true"
        >
          <iconify-icon icon="solar:map-point-linear" class="text-lg"></iconify-icon>
          <span class="hidden lg:inline max-w-[180px] truncate">{{ locationLabel }}</span>
          <iconify-icon icon="solar:alt-arrow-down-linear" class="text-lg text-[#1c4848]/40"></iconify-icon>
        </button>

        <router-link
          to="/login"
          class="inline-flex items-center justify-center px-4 py-2 rounded-2xl text-sm font-semibold border border-[#1c4848]/15 text-[#1c4848] hover:bg-[#1c4848] hover:text-white hover:border-[#1c4848] transition-all shadow-sm"
        >
          {{ t('home.header.login') }}
        </router-link>
      </div>
    </div>

    <SelectCityModal
      v-model="showCityModal"
      v-model:search-query="citySearch"
      :cities="cities"
      :has-more="hasMoreCities"
      :is-loading="citiesLoading"
      :load-error="Boolean(citiesError)"
      :geo-busy="geoStatus === 'requesting' || isLoading"
      @select="onCitySelect"
      @select-coords="onCoordsSelect"
      @use-my-location="onUseMyLocation"
      @reload="reloadCities"
      @load-more="loadMoreCities"
    />
  </header>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { setLocale } from '@/i18n';
import { usePrayerTimes } from '@/composables/usePrayerTimes';
import SelectCityModal from '@/components/home/SelectCityModal.vue';
import LanguageMenu from '@/components/ui/LanguageMenu.vue';
import { quranPath } from '@/utils/quran/paths';

const { t, locale } = useI18n();

// The home page has no language in its URL; the menu sets it for the whole site.
const {
  prayers,
  nextPrayer,
  locationLabel,
  isLoading,
  geoStatus,
  useMyLocation,
  setCity,
  setCoords,
  fetchCities,
} = usePrayerTimes();

const nextKey = computed(() => nextPrayer.value?.key ?? null);

const logoYellowUrl = new URL('../../assets/deenly_logo_yellow.svg', import.meta.url).href;

const showCityModal = ref(false);
const cities = ref([]);
const citiesLoading = ref(false);
const citiesError = ref(null);
const citySearch = ref('');
const nextCitiesPage = ref(1);
const hasMoreCities = computed(() => Number.isFinite(Number(nextCitiesPage.value)) && nextCitiesPage.value != null);

function prayerLabel(key) {
  return t(`home.header.prayers.${key}`);
}

async function loadCities({ reset = false } = {}) {
  citiesLoading.value = true;
  citiesError.value = null;
  try {
    const page = reset ? 1 : nextCitiesPage.value ?? 1;
    const { items, nextPage } = await fetchCities({ page, name: citySearch.value });
    if (reset) cities.value = items;
    else cities.value = [...cities.value, ...items];
    nextCitiesPage.value = nextPage;
  } catch (e) {
    citiesError.value = e;
    cities.value = [];
    nextCitiesPage.value = null;
  } finally {
    citiesLoading.value = false;
  }
}

function reloadCities() {
  nextCitiesPage.value = 1;
  loadCities({ reset: true });
}

function loadMoreCities() {
  if (citiesLoading.value) return;
  if (nextCitiesPage.value == null) return;
  loadCities({ reset: false });
}

async function onCitySelect(city) {
  await setCity(city);
}

async function onCoordsSelect(coords) {
  await setCoords(coords);
}

async function onUseMyLocation() {
  try {
    await useMyLocation();
  } catch {
    // denied or error -> stay on city mode
  }
}

let searchTimer = null;
watch(
  () => citySearch.value,
  () => {
    if (searchTimer) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      reloadCities();
    }, 250);
  }
);

watch(
  () => showCityModal.value,
  (open) => {
    if (!open) return;
    if (cities.value.length === 0) reloadCities();
  }
);

onMounted(() => {
  // Load first page in background so modal opens instantly
  reloadCities();
});
</script>

